import { useEffect, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import api, { fmtError } from "@/lib/api";
import { DataTable, Modal, Btn, Field, TextInput, Textarea, Select, Icons, Badge } from "@/components/ui-lite";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const empty = { code: "", name: "", invoice_currency: "USD", notes: "" };

export default function AggregatorsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/aggregators").then((r) => setRows(r.data));
    api.get("/currencies").then((r) => setCurrencies(r.data));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) await api.put(`/aggregators/${editing.id}`, form);
      else await api.post("/aggregators", form);
      toast.success("Saved"); setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(fmtError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete aggregator?")) return;
    try { await api.delete(`/aggregators/${id}`); load(); }
    catch (e) { toast.error(fmtError(e)); }
  };

  return (
    <AppShell
      title="Aggregators"
      subtitle="Group multiple providers under a single aggregator (the invoicing entity)"
      actions={isAdmin && (
        <Btn data-testid="aggregator-add" onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}>
          <Icons.Plus size={16} className="inline mr-1"/> New aggregator
        </Btn>
      )}
    >
      <DataTable
        testid="aggregators-table"
        columns={[
          { key: "code", label: "Code", mono: true },
          { key: "name", label: "Name" },
          { key: "invoice_currency", label: "Invoice currency", mono: true, render: (r) => <Badge tone={(r.invoice_currency || "USD") === "USD" ? "success" : "info"}>{r.invoice_currency || "USD"}</Badge> },
          { key: "notes", label: "Notes" },
        ]}
        rows={rows}
        actions={isAdmin ? (r) => (
          <div className="flex gap-2">
            <button onClick={() => { setEditing(r); setForm({ code: r.code, name: r.name, invoice_currency: r.invoice_currency || "USD", notes: r.notes || "" }); setOpen(true); }} className="text-[var(--wv-text-2)] hover:text-[var(--wv-primary)]"><Icons.Pencil size={16}/></button>
            <button onClick={() => del(r.id)} className="text-[var(--wv-text-2)] hover:text-[var(--wv-error)]"><Icons.Trash2 size={16}/></button>
          </div>
        ) : null}
      />

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit aggregator" : "New aggregator"}
        footer={<>
          <Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn data-testid="aggregator-save" onClick={save}>Save</Btn>
        </>}>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Code"><TextInput data-testid="aggregator-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="WAC"/></Field>
          <Field label="Name"><TextInput data-testid="aggregator-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Wellness Aggregator Co."/></Field>
          <Field label="Invoice currency">
            <Select data-testid="aggregator-invoice-currency" value={form.invoice_currency || "USD"} onChange={(e) => setForm({ ...form, invoice_currency: e.target.value })}>
              {currencies.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
              {!currencies.find((c) => c.code === "USD") && <option value="USD">USD</option>}
              {!currencies.find((c) => c.code === "EUR") && <option value="EUR">EUR</option>}
            </Select>
          </Field>
          <div/>
          <div className="col-span-2"><Field label="Notes"><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}/></Field></div>
        </div>
      </Modal>
    </AppShell>
  );
}
